import { supabase } from "@/integrations/supabase/client";
import { saveEventSimulation } from "@/components/event-simulator/utils/apiService";
import { getSessionId } from "@/utils/sessionStorage";
import { normalizeDate } from "@/utils/dateUtils";

export interface SimulationData {
  guests: string | number;
  budget: string | number;
  eventDate: string;
  cep: string;
  selectedCategories: string[];
  selectedSuppliers: any;
  selectedDisposableProducts?: any[];
  selectedDecorationItems?: any[];
  selectedSweetsItems?: any[];
  selectedCakeItems?: any[];
  contactEmail?: string;
}

// Helper function to find existing simulation (now queries events table)
const findExistingSimulation = async (sessionId: string, userId?: string): Promise<string | null> => {
  try {
    let query = (supabase as any)
      .from("events")
      .select("id")
      .eq("is_active", true)
      .order("created_at", { ascending: false })
      .limit(1);

    if (userId) {
      query = query.eq("user_id", userId);
    } else {
      query = query
        .is("user_id", null)
        .filter("session_info->>session_id", "eq", sessionId);
    }

    const { data, error } = await query.maybeSingle();
    
    if (error) {
      console.error("Error finding existing simulation:", error);
      return null;
    }

    return data?.id || null;
  } catch (error) {
    console.error("Error finding existing simulation:", error);
    return null;
  }
};

export const saveSimulationForPayment = async (simulationData: SimulationData, userId?: string, source?: string): Promise<string | null> => {
  try {
    const sessionId = userId ? undefined : getSessionId();
    
    // Normalize date input to YYYY-MM-DD preserving the intended local day
    const dateString = normalizeDate(simulationData.eventDate);
    

    // Try to find existing simulation first
    let existingSimulationId: string | null = null;
    if (sessionId || userId) {
      existingSimulationId = await findExistingSimulation(sessionId!, userId);
    }
    
    const eventData = {
      guests: typeof simulationData.guests === 'number' ? simulationData.guests.toString() : simulationData.guests,
      budget: '0',
      date: dateString,
      eventType: 'festa',
      selectedCategories: simulationData.selectedCategories,
      selectedSuppliers: simulationData.selectedSuppliers,
      location: {
        city: 'São Paulo',
        state: 'SP',
        cep: simulationData.cep
      },
      cep: simulationData.cep,
      contactEmail: simulationData.contactEmail
    };
    
    
    if (existingSimulationId) {
      // Update existing event
      const { error } = await (supabase as any)
        .from('events')
        .update({
          event_date: dateString,
          cep: simulationData.cep,
          guests: eventData.guests,
          menu: simulationData.selectedSuppliers,
          status: 'Detalhes da Festa',
          status_payments: 'pending',
          updated_at: new Date().toISOString()
        })
        .eq('id', existingSimulationId);

      if (error) {
        console.error('Error updating existing simulation:', error);
        const simulationId = await saveEventSimulation(eventData, userId, sessionId, undefined, source);
        return simulationId;
      }

      // NOTE: event_client_info is now persisted exclusively via the
      // 'save-event-client-info' edge function (called from InfosFesta),
      // because anonymous/guest sessions cannot write to it under RLS.
      // We intentionally skip the direct client-side update here.

      // Update child events status to 'Detalhes da Festa' as well
      await (supabase as any)
        .from('events')
        .update({ status: 'Detalhes da Festa', updated_at: new Date().toISOString() })
        .eq('parent_event_id', existingSimulationId)
        .eq('is_active', true);

      return existingSimulationId;
    } else {
      // Create new simulation
      const simulationId = await saveEventSimulation(eventData, userId, sessionId, undefined, source);
      return simulationId;
    }
  } catch (error) {
    console.error('Error saving simulation for payment:', error);
    return null;
  }
};

export const linkAnonymousSimulationToUser = async (sessionId: string, userId: string): Promise<boolean> => {
  try {
    const sessionInfo = {
      session_id: sessionId,
      linked_at: new Date().toISOString(),
      linked_from: 'payment_flow'
    };

    const { error } = await (supabase as any)
      .from('events')
      .update({ 
        user_id: userId,
        session_info: sessionInfo,
        status_payments: 'authenticated',
        updated_at: new Date().toISOString()
      })
      .is('user_id', null)
      .filter('session_info->>session_id', 'eq', sessionId);

    if (error) {
      console.error('Error linking anonymous simulation to user:', error);
      return false;
    }
    
    // Sync event_client_info - find event IDs by session
    const { data: linkedEvents } = await (supabase as any)
      .from('events')
      .select('id')
      .eq('user_id', userId)
      .filter('session_info->>session_id', 'eq', sessionId);
    
    if (linkedEvents?.length) {
      for (const evt of linkedEvents) {
        await syncEventClientInfoFromProfile(evt.id, userId);
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error linking anonymous simulation to user:', error);
    return false;
  }
};

export const canAccessPayment = async (simulationId: string, userId?: string, sessionId?: string): Promise<boolean> => {
  try {
    const { data, error } = await (supabase as any)
      .from('events')
      .select('user_id, session_info')
      .eq('id', simulationId)
      .eq('is_active', true)
      .maybeSingle();

    if (error || !data) {
      console.error('Error checking payment access:', error);
      return false;
    }

    if (userId && data.user_id === userId) {
      return true;
    }

    if (!data.user_id && sessionId && data.session_info) {
      const si = data.session_info as any;
      if (si.session_id === sessionId) {
        return true;
      }
    }

    return false;
  } catch (error) {
    console.error('Error checking payment access:', error);
    return false;
  }
};

export const initializePayment = async (simulationId: string, userId?: string): Promise<{
  success: boolean;
  paymentSessionId?: string;
  simulationData?: any;
  error?: string;
}> => {
  try {
    const { data: simulation, error: simError } = await (supabase as any)
      .from('events')
      .select('*')
      .eq('id', simulationId)
      .eq('is_active', true)
      .maybeSingle();

    if (simError || !simulation) {
      return { success: false, error: 'agora estaaparecendo que a silmulação nao foi encontrada' };
    }

    const sessionInfo = simulation.session_info as any;
    const hasAccess = await canAccessPayment(simulationId, userId, sessionInfo?.session_id);
    if (!hasAccess) {
      return { success: false, error: 'Acesso negado à simulação' };
    }

    await (supabase as any)
      .from('events')
      .update({ 
        status_payments: 'processing',
        updated_at: new Date().toISOString()
      })
      .eq('id', simulationId);

    return {
      success: true,
      simulationData: simulation
    };
  } catch (error) {
    console.error('Error initializing payment:', error);
    return { success: false, error: 'Erro interno do servidor' };
  }
};

export const getUserActiveSimulations = async (userId: string) => {
  try {
    const { data, error } = await (supabase as any)
      .from('events')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(5);

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting user simulations:', error);
    return [];
  }
};

export const linkSessionToUser = async (sessionId: string, userId: string) => {
  try {
    const sessionInfo = {
      session_id: sessionId,
      linked_at: new Date().toISOString(),
      linked_from: 'user_login'
    };

    const { error } = await (supabase as any)
      .from('events')
      .update({ 
        user_id: userId,
        session_info: sessionInfo,
        updated_at: new Date().toISOString()
      })
      .is('user_id', null)
      .filter('session_info->>session_id', 'eq', sessionId);

    if (error) throw error;
    
    // Sync event_client_info with profile
    const { data: linkedEvents } = await (supabase as any)
      .from('events')
      .select('id')
      .eq('user_id', userId)
      .filter('session_info->>session_id', 'eq', sessionId);
    
    if (linkedEvents?.length) {
      for (const evt of linkedEvents) {
        await syncEventClientInfoFromProfile(evt.id, userId);
      }
    }
    
  } catch (error) {
    console.error('Error linking session to user:', error);
  }
};

export const linkAnonymousSimulationByIdToUser = async (simulationId: string, userId: string): Promise<boolean> => {
  try {

    
    // Ensure profile exists before linking (FK constraint: events.user_id -> profile.id)
    const { data: profileExists } = await (supabase as any)
      .from('profile')
      .select('id')
      .eq('id', userId)
      .maybeSingle();
    
    if (!profileExists) {

      const { data: authData } = await supabase.auth.getUser();
      const userEmail = authData?.user?.email || '';
      const userName = authData?.user?.user_metadata?.full_name || userEmail.split('@')[0] || '';
      
      const { error: profileError } = await (supabase as any)
        .from('profile')
        .insert({
          id: userId,
          email: userEmail,
          full_name: userName,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_active: true
        });
      
      if (profileError) {
        // If conflict (profile was created by trigger in the meantime), that's fine
        if (profileError.code !== '23505') {
          console.error('❌ Failed to create profile:', profileError);
          // Don't block the flow - the insert in events will fail if profile truly doesn't exist
        } else {
        }
      } else {
      }
    }
    
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000;
    
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {

      
      const { data: sessionData } = await supabase.auth.getSession();
      
      if (!sessionData.session || sessionData.session.user?.id !== userId) {
        console.warn('⚠️ Session mismatch or no session, waiting for authentication...');
        if (attempt < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
          continue;
        }
      }
      
      const { data: existingSimulation, error: fetchError } = await (supabase as any)
        .from('events')
        .select('id, user_id, session_info, status_payments')
        .eq('id', simulationId)
        .maybeSingle();
      
      if (fetchError) {
        console.error('❌ Error fetching simulation:', fetchError);
        if (attempt === MAX_RETRIES) return false;
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        continue;
      }
      
      if (!existingSimulation) {
        console.warn('⚠️ Simulation not found for id:', simulationId);
        return false;
      }
      
      if (existingSimulation.user_id !== null) {
        if (existingSimulation.user_id === userId) {
          // Don't update status here - let the page flow control status transitions
          return true;
        } else {
          console.error('❌ Simulation is linked to a different user');
          return false;
        }
      }

      const existingSessionInfo = existingSimulation.session_info as any || {};
      const sessionInfo = {
        ...existingSessionInfo,
        linked_at: new Date().toISOString(),
        linked_from: 'infos_festa_page',
        linking_attempt: attempt
      };

      const { data: updateResult, error } = await (supabase as any)
        .from('events')
        .update({ 
          user_id: userId,
          session_info: sessionInfo,
          status_payments: 'authenticated',
          updated_at: new Date().toISOString()
        })
        .eq('id', simulationId)
        .is('user_id', null)
        .select();

      if (error) {
        console.error(`❌ Error linking (attempt ${attempt}):`, error);
        if (attempt === MAX_RETRIES) return false;
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        continue;
      }
      
      if (!updateResult || updateResult.length === 0) {
        console.warn(`⚠️ No rows updated (attempt ${attempt})`);
        if (attempt === MAX_RETRIES) {
          const { data: finalCheck } = await (supabase as any)
            .from('events')
            .select('user_id')
            .eq('id', simulationId)
            .maybeSingle();
          
          return finalCheck?.user_id === userId;
        }
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        continue;
      }
      
      
      // Sync event_client_info with user profile data
      await syncEventClientInfoFromProfile(simulationId, userId);
      
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('❌ Fatal error linking anonymous simulation by ID to user:', error);
    return false;
  }
};

/**
 * After linking an event to a user, update event_client_info with profile data
 */
const syncEventClientInfoFromProfile = async (eventId: string, userId: string): Promise<void> => {
  try {
    const { data: profile, error: profileError } = await (supabase as any)
      .from('profile')
      .select('full_name, phone, email')
      .eq('id', userId)
      .maybeSingle();

    if (profileError || !profile) {
      console.warn('⚠️ Could not fetch profile for event_client_info sync:', profileError);
      return;
    }

    await (supabase as any)
      .from('event_client_info')
      .upsert({
        event_id: eventId,
        client_name: profile.full_name || null,
        client_phone: profile.phone || null,
        client_email: profile.email || null,
        updated_at: new Date().toISOString()
      }, { onConflict: 'event_id' });

  } catch (error) {
    console.warn('⚠️ Failed to sync event_client_info:', error);
  }
};
